import { useAuth } from '@/context/AuthContext';
import { useSettings } from '@/hooks/useSettings';
import { usePathname, useRouter } from 'next/navigation';
import type { EditorInstance, JSONContent } from 'novel';
import { useEffect, useMemo, useState } from 'react';
import type { CitationAttributes } from './citation-extension';
import CitationItem from './citations-item';

interface CitationProps {
  defaultContent: JSONContent;
  editor: EditorInstance;
}

export const getCitations = (node: JSONContent): CitationAttributes[] => {
  let citations: CitationAttributes[] = [];

  if (node.type === 'paragraph' && node.content) {
    for (const childNode of node.content) {
      if (childNode.type === 'citation' && childNode.attrs?.citation) {
        citations.push(childNode.attrs as CitationAttributes);
      }
    }
  }
  if (node.content) {
    for (const child of node.content) {
      citations = citations.concat(getCitations(child));
    }
  }
  return citations;
};

const Citations = ({ defaultContent, editor }: CitationProps) => {
  const [content, setContent] = useState<JSONContent | null>(defaultContent);
  const { user } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const { setReturnPage } = useSettings();
  const citations = useMemo(() => {
    if (!content) return [];
    return getCitations(content);
  }, [content]);

  useEffect(() => {
    if (editor) {
      editor.on('update', ({ editor }) => {
        setContent(editor.getJSON());
      });
    }
  }, [editor]);

  if (!citations.length) return null;

  // const citationFormats = [
  //   { value: 'APA_7', label: 'APA (7th edition)' },
  //   { value: 'MLA_9', label: 'MLA (9th edition)' },
  //   { value: 'HARVARD', label: 'Harvard' },
  //   { value: 'CHICAGO', label: 'Chicago' },
  // ];

  // const currentFormatLabel = citationFormats.find(
  //   (format) => format.value === (user?.citationFormat || 'APA_7'),
  // )?.label;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="flex flex-col items-start gap-1">
          <p className="font-semibold text-3xl">References</p>
          <span className="text-sm text-textGray2">
            Change the citation format in{' '}
            <span
              className="underline cursor-pointer hover:text-foreground hover:opacity-80 transition ease-in-out duration-150"
              onClick={() => {
                setReturnPage(pathname ?? '/new');
                router.push('/settings/preferences');
              }}
            >
              settings
            </span>
            .
          </span>
        </div>
      </div>
      <ol className="list-decimal list-inside flex flex-col gap-2">
        {citations.map(({ citation }, index) => (
          <CitationItem
            // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
            key={citation.id + index}
            citation={citation}
            format={user?.citationFormat || 'APA_7'}
          />
        ))}
      </ol>
    </div>
  );
};

export default Citations;
